/**
 * THE NEW DESIGN'S OWN NOON / DUSK (design-system §7a, 2026-09-23).
 *
 * The live site's theme lives in localStorage `ui_theme` and is applied to
 * <html data-theme> before first paint (PR-1). The preview must NOT write it:
 * looking at dusk under /prest0design/ would otherwise turn prest0.ai dark for
 * that browser, half-dark (623 hardcoded hexes, C8), which is exactly what the
 * developer-org gate on that toggle exists to prevent. So the preview keeps
 * its choice under its own key and applies it to the new design's root
 * (`.pv[data-theme]`) only — never to <html>.
 *
 * Resolution, first match wins: a `?theme=dusk|noon` in the address (a link
 * that opens the page at dusk, for a demonstration); the visitor's own choice
 * in this browser; the device's colour scheme (D-3's "system" default, which
 * the live site adopts only at PR-6 — the preview is where that is seen first).
 *
 * On the first download (the door reads it to paint the right sky while the
 * chunk arrives), so keep it small. Every storage access is guarded: storage
 * can throw in a private window.
 */

export type PreviewTheme = 'light' | 'dark';
export type PreviewThemeChoice = PreviewTheme | 'system';

export const PREVIEW_THEME_KEY = 'prest0design_theme';

export function readPreviewThemeChoice(): PreviewThemeChoice {
  try {
    const v = window.localStorage.getItem(PREVIEW_THEME_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function writePreviewThemeChoice(choice: PreviewThemeChoice): void {
  try {
    if (choice === 'system') window.localStorage.removeItem(PREVIEW_THEME_KEY);
    else window.localStorage.setItem(PREVIEW_THEME_KEY, choice);
  } catch {
    /* storage unavailable: the choice lasts for this page only */
  }
}

/** `?theme=dusk` / `?theme=noon` (or dark / light) in the address. */
export function themeFromAddress(search: string = typeof window !== 'undefined' ? window.location.search : ''): PreviewTheme | null {
  try {
    const v = new URLSearchParams(search).get('theme');
    if (v === 'dusk' || v === 'dark') return 'dark';
    if (v === 'noon' || v === 'light') return 'light';
  } catch {
    /* an unparsable query names no theme */
  }
  return null;
}

export function deviceTheme(): PreviewTheme {
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  } catch {
    return 'light';
  }
}

export function resolvePreviewTheme(choice: PreviewThemeChoice = readPreviewThemeChoice()): PreviewTheme {
  const fromAddress = themeFromAddress();
  if (fromAddress) return fromAddress;
  if (choice !== 'system') return choice;
  return deviceTheme();
}

/**
 * THE PAGE'S LIGHT, FOR THE CHAT (PR-4a.2, 2026-09-25).
 *
 * The chat floats over whichever page is showing, so it takes that page's light. Read live the day after the
 * home page went live: a visitor whose device is set to dark saw prest0.ai/ at dusk under a pale noon chat.
 * A page built for dusk says so on <html data-design-light="dusk"> while it is on screen (./PageLight.tsx);
 * today's pages are light only (D-66) and say nothing, so the chat stays at noon over them. Never
 * <html data-theme> and never `ui_theme`: prest0.ai's own theme mechanism is not the new design's (D-66).
 */
export const PAGE_LIGHT_ATTR = 'data-design-light';

/** Put the page's light on <html> (dusk), or take it off (noon, or no page built for dusk on screen). */
export function publishPageLight(
  theme: PreviewTheme | null,
  doc: Document | undefined = typeof document !== 'undefined' ? document : undefined,
): void {
  if (!doc) return;
  if (theme === 'dark') doc.documentElement.setAttribute(PAGE_LIGHT_ATTR, 'dusk');
  else doc.documentElement.removeAttribute(PAGE_LIGHT_ATTR);
}
